import {
  getGuildWarPathByCode,
  getGuildWarPathByKey,
  isMartialPathCode,
  isMartialPathKey,
  type GuildWarPathEntry,
  type MartialPathCode,
  type MartialPathKey,
} from "@/core/guild-war/path-registry"

export type GuildWarPathValue = string | null | undefined

export const toGuildWarPathKey = (
  value: GuildWarPathValue,
): MartialPathKey | null => {
  if (!value) {
    return null
  }

  if (isMartialPathKey(value)) {
    return value
  }

  if (isMartialPathCode(value)) {
    return getGuildWarPathByCode(value).key
  }

  return null
}

export const toGuildWarPathCode = (
  value: GuildWarPathValue,
): MartialPathCode | null => {
  if (!value) {
    return null
  }

  if (isMartialPathCode(value)) {
    return value
  }

  if (isMartialPathKey(value)) {
    return getGuildWarPathByKey(value).code
  }

  return null
}

export const resolveGuildWarPath = (
  value: GuildWarPathValue,
): GuildWarPathEntry | null => {
  if (!value) {
    return null
  }

  if (isMartialPathKey(value)) {
    return getGuildWarPathByKey(value)
  }

  if (isMartialPathCode(value)) {
    return getGuildWarPathByCode(value)
  }

  return null
}

export const toGuildWarPathShortLabel = (
  value: GuildWarPathValue,
): string | null => {
  return resolveGuildWarPath(value)?.shortLabel ?? null
}

export const toGuildWarPathFullLabel = (
  value: GuildWarPathValue,
): string | null => {
  return resolveGuildWarPath(value)?.fullLabel ?? null
}

export const toGuildWarPathDisplayLabel = (
  value: GuildWarPathValue,
  options?: {
    variant?: "short" | "full"
    includeEmoji?: boolean
    fallback?: string
  },
): string => {
  const path = resolveGuildWarPath(value)
  if (!path) {
    return options?.fallback ?? value ?? ""
  }

  const base = options?.variant === "full" ? path.fullLabel : path.shortLabel

  if (options?.includeEmoji) {
    return `${path.emoji} ${base}`
  }

  return base
}

export const toGuildWarPathPairDisplayLabel = (
  primary: GuildWarPathValue,
  secondary: GuildWarPathValue,
  options?: {
    variant?: "short" | "full"
    includeEmoji?: boolean
    noneLabel?: string
    separator?: string
  },
): string => {
  const primaryLabel = toGuildWarPathDisplayLabel(primary, {
    variant: options?.variant,
    includeEmoji: options?.includeEmoji,
    fallback: typeof primary === "string" ? primary : "",
  }).trim()
  const secondaryLabel = toGuildWarPathDisplayLabel(secondary, {
    variant: options?.variant,
    includeEmoji: options?.includeEmoji,
    fallback: typeof secondary === "string" ? secondary : "",
  }).trim()

  if (!primaryLabel && !secondaryLabel) {
    return options?.noneLabel ?? "None"
  }

  if (!secondaryLabel) {
    return primaryLabel
  }

  if (!primaryLabel) {
    return secondaryLabel
  }

  return `${primaryLabel}${options?.separator ?? " + "}${secondaryLabel}`
}

export const getGuildWarPathSearchTokens = (
  value: GuildWarPathValue,
): string[] => {
  if (!value) {
    return []
  }

  const path = resolveGuildWarPath(value)
  if (!path) {
    return [value]
  }

  return [
    value,
    path.key,
    path.code,
    path.shortLabel,
    path.fullLabel,
    `${path.emoji} ${path.shortLabel}`,
  ]
}
